import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ContactPickerField, SelectedContact } from '@extension/context/content/components/shared';
import { useWaContactsLazyLoad } from '@extension/context/content/hooks/useWaContactsLazyLoad';
import { namespace } from '@common-config/constants';
import {
  backendApi,
  CreateMyBridgeAgentParams,
  MyBridgeAgent,
  MyBridgeAgentPolicy,
  MyBridgeReadPolicyInput,
  MyBridgeSchedule,
  MyBridgeWebAccess,
} from '@common/reducers/backend-api/backend-api';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Check, ChevronDown, Info, Loader2, Maximize2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MarkdownEditorModal } from './MarkdownEditorModal';
import { KnowledgeFilesManager, uploadPendingKnowledgeFiles, type PendingKnowledgeFile } from './KnowledgeFilesManager';
import { GoogleCalendarSection } from './GoogleCalendarSection';
import { toast } from 'sonner';
import { McpServersSection } from './McpServersSection';
import { TikSection } from './TikSection';
import { TimeTriggersEditor } from './TimeTriggersEditor';
import { useBridgeChatNames } from './useBridgeChatNames';
import {
  takeoverFormFrom,
  takeoverPayload,
  autoResumeInvalid,
  AUTO_RESUME_DEFAULT_MINUTES,
  AUTO_RESUME_MIN,
  AUTO_RESUME_MAX,
  type TakeoverFormFields,
} from './agent-takeover';
import {
  QUIET_DAYS,
  QUIET_HOURS_MAX_WINDOWS,
  newQuietWindow,
  quietHoursError,
  quietHoursFormFrom,
  quietHoursPayload,
  type QuietHoursFormFields,
} from './agent-quiet-hours';
import type { MyBridgeQuietWindow } from '@common/reducers/backend-api/backend-api';
import { reportAgentEvent } from '@common/services/agent-analytics';
import { PausedOfferField } from './PausedChats';
import { VoicePicker } from './VoicePicker';
import { AgentBoardsSection } from './AgentBoardsSection';

/** The standalone card's column: the 640px fields + their padding, white. */
const CARD_COLUMN = 'mx-auto w-full max-w-[720px] bg-ai-surface';

interface AgentFormProps {
  /** When set the form edits this agent; otherwise it creates a new one. */
  agent?: MyBridgeAgent | null;
  isRtl?: boolean;
  /**
   * Bridge operator/admin — unlocks the operator-only Advanced fields (model,
   * effort, level, timeout, memory). Voice, the bot marker and web access are
   * the owner's and show for everyone.
   */
  isAdmin?: boolean;
  /** The agent's deterministic capability contract (incoming/send/tools). */
  policy?: MyBridgeAgentPolicy;
  /** All of the user's time triggers; the editor filters to this agent. */
  schedules?: MyBridgeSchedule[];
  /** A save succeeded: `created` = it created the agent (`agentId` is the new one). */
  onSaved?: (agentId: string, created: boolean) => void;
  /** Standalone (no chat beside it): the form is its own white card on the page
   *  background, and the whole pane scrolls (one scrollbar, at the window edge). */
  card?: boolean;
}

/** A free-text host list (newlines/commas/spaces) → unique, lower-cased hosts. */
export const parseHosts = (raw: string): string[] =>
  Array.from(new Set(raw.split(/[\s,]+/).map((h) => h.toLowerCase()).filter(Boolean)));

type WebMode = MyBridgeWebAccess['mode'];
type ChatListField = 'chatIds' | 'sendChatIds' | 'readMsgChatIds';
type KeysOf<T> = { [K in keyof FormState]: FormState[K] extends T ? K : never }[keyof FormState];

/**
 * Everything the form edits. One object → one "dirty" signature, one load, one reset.
 * TakeoverFormFields = rev 3.3 takeover defaults + operator chat (agent-takeover.ts).
 */
export interface FormState extends TakeoverFormFields, QuietHoursFormFields {
  label: string;
  identityPrompt: string;
  knowledgeText: string;
  // WHICH chats trigger it (a required decision), mirroring the bridge form: allow
  // list / all-except block list. `includeNewChats`
  // (respond to new incoming chats) is orthogonal.
  chatMode: 'list' | 'all_except';
  chatIds: string[];
  includeNewChats: boolean;
  // chatTypes: checked = 'private' (one-to-one chats only, never groups); unchecked = 'all' (default).
  privateChatsOnly: boolean;
  // WHERE it may SEND — separate from what triggers it. 'same' = it replies in the
  // chat that triggered it; 'list' / 'all_except' = its own allow / block list.
  sendMode: 'same' | 'list' | 'all_except';
  sendChatIds: string[];
  // TRIGGERS / TOOLS — the deterministic, server-enforced policy. Incoming off ⇒
  // time-triggered only. Tools = the READ-ONLY MCP (sending is the Bridge's job);
  // `readMessages` = its OWN chat + `readMsgChatIds` — never any chat, and no chat
  // list/search (F7, server-enforced).
  incomingEnabled: boolean;
  toolsWhatsappMcp: boolean;
  /** May answer with a WhatsApp voice note (policy tools.voice). */
  toolsVoice: boolean;
  /** WHEN to answer with a voice note instead of text — goes into its prompt (policy voiceWhen). '' = the default rule. */
  voiceWhen: string;
  /** Answers WhatsApp calls to its chats with a live voice conversation (policy tools.calls). */
  toolsCalls: boolean;
  /** The voice it speaks with — voice notes and calls (policy voiceName). '' = the provider default. */
  voiceName: string;
  /** Manages its own Blueticks boards (policy tools.boards). */
  toolsBoards: boolean;
  /** Existing boards picked for a kind, adopted on save. */
  adoptBoards: { boardId: string; kind: string }[];
  // Creative tools (songs via Suno, images) — the agent sees each only while it is on.
  toolsSuno: boolean;
  toolsImages: boolean;
  readMessages: boolean;
  readMsgChatIds: string[];
  // Customer-writable KB behaviour.
  knowledgeOnly: boolean;
  stayOnTopic: boolean;
  // Advanced, customer-writable (lib-bridge customer-policy `agentFields`): the
  // voice-transcription language, the 🤖 marker and web access. '' = the bridge default.
  transcribeLang: string;
  hideBotPrefix: boolean;
  webMode: WebMode;
  /** Free text (newlines/commas), parsed on save. */
  webHosts: string;
  // Advanced, operator-only — sent only for an admin. '' = the bridge default.
  model: string;
  effort: string;
}

/** The form as loaded from an agent + its policy (safe defaults for a new agent). */
export const formFrom = (agent: MyBridgeAgent | null | undefined, policy: MyBridgeAgentPolicy | undefined): FormState => ({
  label: agent?.label ?? '',
  identityPrompt: agent?.identityPrompt ?? '',
  knowledgeText: agent?.knowledgeText ?? '',
  chatMode: agent?.chatScope === 'all_except' ? 'all_except' : 'list',
  // In 'all_except' the list the user edits is the BLOCK list, which the agent
  // stores in `chatBlockList` (its `chatIds` is empty). Reading `chatIds` there
  // showed an empty picker and a save then wiped the block list.
  chatIds: (agent?.chatScope === 'all_except' ? agent?.chatBlockList : agent?.chatIds) ?? [],
  includeNewChats: agent?.includeNewChats ?? false,
  privateChatsOnly: agent?.chatTypes === 'private',
  sendMode: agent?.sendScope ?? 'same',
  sendChatIds: agent?.sendChatIds ?? [],
  incomingEnabled: policy?.incoming?.enabled ?? true,
  toolsWhatsappMcp: policy?.tools?.whatsappMcp ?? false,
  toolsVoice: policy?.tools?.voice ?? false,
  voiceWhen: policy?.voiceWhen ?? '',
  toolsCalls: policy?.tools?.calls ?? false,
  voiceName: policy?.voiceName ?? '',
  toolsBoards: policy?.tools?.boards ?? false,
  adoptBoards: [],
  toolsSuno: policy?.tools?.suno ?? false,
  toolsImages: policy?.tools?.images ?? false,
  readMessages: policy?.read?.messages ?? false,
  readMsgChatIds: policy?.read?.messageChatIds ?? [],
  knowledgeOnly: agent?.knowledgeOnly ?? false,
  stayOnTopic: agent?.stayOnTopic ?? false,
  model: agent?.model ?? '',
  effort: agent?.effort ?? '',
  transcribeLang: agent?.transcribeLang ?? '',
  hideBotPrefix: agent?.hideBotPrefix ?? false,
  // rev 3.3: takeover defaults + operator chat.
  ...takeoverFormFrom(agent),
  // #464: quiet hours.
  ...quietHoursFormFrom(agent),
  webMode: agent?.webAccess?.mode ?? 'off',
  webHosts: (agent?.webAccess?.hosts ?? []).join('\n'),
});

/**
 * The agent save body for a form state. `loaded` = the form as it was loaded
 * (the takeover fields are sent only where they changed against it).
 *
 * Customer-writable fields are sent for everyone; operator-only fields ONLY for
 * an admin — the Bridge 403s the WHOLE save when a customer's request carries
 * any of them.
 */
export function buildAgentPayload(f: FormState, loaded: FormState, isAdmin: boolean): CreateMyBridgeAgentParams {
  // Map the incoming-trigger chats to the Bridge fields. When incoming is OFF the
  // agent is time-triggered only, so its allow-list is CLEARED — the Bridge then
  // never polls or routes it (that is the enforcement of "incoming off"). The
  // `incomingEnabled:false` flag also relaxes the create-time "at least one chat"
  // rule on the backend; it is not stored on the Bridge agent record.
  const scopeFields = !f.incomingEnabled
    ? { chatScope: 'list', chatIds: [], chatBlockList: [], includeNewChats: false, incomingEnabled: false }
    : f.chatMode === 'all_except'
      ? { chatScope: 'all_except', chatBlockList: f.chatIds, chatIds: [], includeNewChats: f.includeNewChats, incomingEnabled: true }
      : { chatScope: 'list', chatIds: f.chatIds, includeNewChats: f.includeNewChats, incomingEnabled: true };

  return {
    label: f.label.trim(),
    identityPrompt: f.identityPrompt.trim(),
    ...scopeFields,
    // Always sent, so unchecking is a real change back to 'all'.
    chatTypes: f.incomingEnabled && f.privateChatsOnly ? 'private' : 'all', // control is hidden when incoming is off
    // Where it may send — always sent, so switching back to 'same' is a real change.
    sendScope: f.sendMode,
    sendChatIds: f.sendMode === 'same' ? [] : f.sendChatIds,
    // Always sent, so CLEARING the knowledge really clears it (sending it only
    // when non-empty left the old text on the agent forever).
    knowledgeText: f.knowledgeText.trim(),
    knowledgeOnly: f.knowledgeOnly,
    stayOnTopic: f.stayOnTopic,
    // rev 3.3 takeover defaults + operator chat — only what changed against what
    // was loaded.
    ...takeoverPayload(loaded, f),
    // #464 quiet hours — only when changed against what was loaded.
    ...quietHoursPayload(loaded, f),
    // Advanced — the customer-writable part (lib-bridge customer-policy `agentFields`).
    // null = back to the bridge default, so a voice override can be UNDONE.
    transcribeLang: f.transcribeLang || null,
    hideBotPrefix: f.hideBotPrefix,
    // Always sent (so turning it OFF is a real change); hosts only matter in allowlist mode.
    webAccess: { mode: f.webMode, hosts: f.webMode === 'allowlist' ? parseHosts(f.webHosts) : [] },
    // Operator-only fields — admin only (see above).
    ...(isAdmin
      ? {
          model: f.model || null,
          effort: f.effort || null,
        }
      : {}),
  };
}

/**
 * The agent's capability contract as saved (agent-policy). The READ policy only
 * means something with tools on; off clears it (null). F7: an agent reads only
 * its own chat + `messageChatIds`; the chat list (`read.chats`) is never granted,
 * so it is not sent.
 */
export function buildPolicyPayload(f: FormState): {
  incomingEnabled: boolean;
  toolsWhatsappMcp: boolean;
  toolsVoice: boolean;
  voiceWhen: string;
  toolsCalls: boolean;
  voiceName: string;
  toolsBoards: boolean;
  adoptBoards?: { boardId: string; kind: string }[];
  toolsSuno: boolean;
  toolsImages: boolean;
  read: MyBridgeReadPolicyInput | null;
} {
  return {
    incomingEnabled: f.incomingEnabled,
    toolsWhatsappMcp: f.toolsWhatsappMcp,
    toolsVoice: f.toolsVoice,
    voiceWhen: f.voiceWhen.trim(),
    toolsCalls: f.toolsCalls,
    voiceName: f.voiceName,
    toolsBoards: f.toolsBoards,
    ...(f.toolsBoards && f.adoptBoards.length ? { adoptBoards: f.adoptBoards } : {}),
    toolsSuno: f.toolsSuno,
    toolsImages: f.toolsImages,
    read: f.toolsWhatsappMcp ? { messages: f.readMessages, messageChatIds: f.readMessages ? f.readMsgChatIds : [] } : null,
  };
}

interface FieldErrors {
  label?: string;
  identityPrompt?: string;
  chatIds?: string;
  sendChatIds?: string;
  operatorChatId?: string;
  quietHours?: string;
  autoResume?: string;
}

const SELECT_CONTENT_PROPS = { zIndex: 30000, className: 'min-w-[var(--radix-select-trigger-width)]' };
/**
 * The form's look (Agents 2.0 / instinct): no boxes and no rules between
 * sections - whitespace and a quiet heading carry the structure; fields are soft
 * filled surfaces that lift to white with a mint halo on focus.
 */
const sectionCls = 'space-y-3';
const sectionTitleCls = 'text-[15px] font-semibold tracking-[-0.01em] text-ai-text';
const labelCls = 'text-[13px] font-medium text-ai-text';
const hintCls = 'text-[13px] leading-5 text-ai-text-secondary';
const fieldCls =
  // `!`: the base Input/Textarea colour classes are ones tailwind-merge can't pair with
  // our token classes, so the fill/border would otherwise lose on CSS order.
  'rounded-xl border-transparent! bg-ai-muted! px-3.5 shadow-none! transition-[background-color,box-shadow] duration-(--ai-duration-fast) ease-ai placeholder:text-ai-text-faint hover:bg-[color-mix(in_srgb,var(--ai-text)_6%,transparent)]! focus-visible:bg-ai-surface! focus-visible:ring-0 focus-visible:shadow-[0_0_0_1px_var(--ai-border-strong),0_0_0_4px_var(--ai-accent-soft)]!';
const inputCls = `h-11 ${fieldCls}`;
/** The chat picker's chip field, in the same soft-field look. */
const pickerBoxCls =
  'min-h-11 rounded-xl border-transparent bg-ai-muted ring-0 transition-[background-color,box-shadow] duration-(--ai-duration-fast) hover:bg-[color-mix(in_srgb,var(--ai-text)_6%,transparent)] focus-within:bg-ai-surface focus-within:shadow-[0_0_0_1px_var(--ai-border-strong),0_0_0_4px_var(--ai-accent-soft)] [&_input]:bg-transparent!';
const textareaCls = `py-3 leading-6 ${fieldCls}`;
/** IANA zones for the picker's suggestions (empty where the runtime cannot list them). */
const supportedTimeZones = (): string[] => {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] };
  try {
    return intl.supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
};
const ErrorText: React.FC<{ error?: string | null }> = ({ error }) => (error ? <p className="text-sm text-red-500">{error}</p> : null);

/**
 * THE agent form — one for create and edit (New and Edit in the agent workspace,
 * web page and extension dialog alike). It fills its pane: the fields scroll, the
 * save bar stays pinned under them.
 * Create POSTs a new agent; edit PATCHes it; both then save the agent's policy.
 * When the AI builder changes the agent, the form reloads silently if it is clean,
 * or offers to load the change if there are unsaved edits.
 */
export const AgentForm: React.FC<AgentFormProps> = ({
  agent,
  isRtl,
  isAdmin,
  policy,
  schedules,
  onSaved,
  card,
}) => {
  const { t } = useTranslation(namespace);
  const agentId = agent?.id;
  const dir = isRtl ? 'rtl' : 'ltr';
  // Typed text: per its own script (dir=auto). Empty: the page direction, so the
  // placeholder sits on the reading side (dir=auto on an empty field falls back to LTR).
  const textDir = (value: string) => (value ? 'auto' : dir);

  const [form, setForm] = useState<FormState>(() => formFrom(null, undefined));
  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));
  const setQuietWindow = (i: number, patch: Partial<MyBridgeQuietWindow>) =>
    setForm((f) => ({ ...f, quietWindows: f.quietWindows.map((w, j) => (j === i ? { ...w, ...patch } : w)) }));
  // Contacts picked in this session (avatar/name); other ids resolve via nameOf.
  const [picked, setPicked] = useState<Record<string, SelectedContact>>({});
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  // The Save button itself confirms the save: spinner while in flight, then a check.
  const [justSaved, setJustSaved] = useState(false);
  useEffect(() => {
    if (!justSaved) return;
    const timer = setTimeout(() => setJustSaved(false), 2000);
    return () => clearTimeout(timer);
  }, [justSaved]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Big Markdown editors for the prompt + knowledge (comfortable for long text).
  const [promptEditorOpen, setPromptEditorOpen] = useState(false);
  const [knowledgeEditorOpen, setKnowledgeEditorOpen] = useState(false);
  const [remoteChanged, setRemoteChanged] = useState(false);
  // FE#484: "Also resume the paused chats" (offered when auto-takeover is off).
  const [alsoResumePaused, setAlsoResumePaused] = useState(false);
  const resumePausedRef = useRef<(() => Promise<unknown>) | null>(null);

  // Load the WhatsApp contacts into the store while the form is mounted, so the
  // shared contact picker (which reads the cache) actually has chats to show — the
  // my-bridge page never mounts a team-inbox that would otherwise load them.
  useWaContactsLazyLoad({ searchFilter: '', enabled: true });

  const [createAgent, { isLoading: isCreating }] = backendApi.useCreateMyBridgeAgentMutation();
  const [updateAgent, { isLoading: isUpdating }] = backendApi.useUpdateMyBridgeAgentMutation();
  const [setAgentPolicy, { isLoading: isSavingPolicy }] = backendApi.useSetMyBridgeAgentPolicyMutation();
  const isSaving = isCreating || isUpdating || isSavingPolicy;
  // A NEW agent's chosen integrations: sent with the create, attached unconnected by the backend.
  const [mcpPicks, setMcpPicks] = useState<string[]>([]);
  // A NEW agent's knowledge files + calendar, held until its first save (like attachments
  // in a chat before Send); an existing agent's sections talk to the server directly.
  const [pendingFiles, setPendingFiles] = useState<PendingKnowledgeFile[]>([]);
  const [uploadKnowledgeFile] = backendApi.useUploadMyBridgeAgentKnowledgeFileMutation();

  // Fetch the agent's policy + schedules FRESH whenever an agent is loaded, so it
  // never shows stale data from a not-yet-refetched overview (a just-built agent
  // used to open with empty triggers/tools).
  // `currentData` — NOT `data`: `data` keeps the PREVIOUS agent's result while the
  // new one loads, which would load agent A's policy into agent B's form.
  const freshArgs = { skip: !agentId, refetchOnMountOrArgChange: true };
  const { currentData: freshPolicy, isFetching: policyFetching } = backendApi.useGetMyBridgeAgentPolicyQuery({ agentId: agentId ?? '' }, freshArgs);
  const { currentData: freshSchedules } = backendApi.useGetMyBridgeAgentSchedulesQuery({ agentId: agentId ?? '' }, freshArgs);
  // A service-account calendar connected before Integrations existed: the only case its section still shows.
  const { currentData: legacyCalendar } = backendApi.useGetMyBridgeAgentCalendarQuery({ agentId: agentId ?? '' }, { skip: !agentId });
  const { currentData: agentTik } = backendApi.useGetMyBridgeAgentTikQuery({ agentId: agentId ?? '' }, { skip: !agentId });
  const effectivePolicy = freshPolicy ?? policy;
  // Editing with NO policy in hand yet (no overview fallback, first fetch in
  // flight): the Triggers/Tools checkboxes are still showing defaults, so saving
  // now would write those defaults over the real contract.
  const policyUnknown = !!agentId && !effectivePolicy && policyFetching;
  const effectiveSchedules = freshSchedules ?? schedules ?? [];

  // Shows "My Love ❤️" instead of the raw number the agent stores.
  // rev 3.3 §6.17.1: no connection/project picker — the account is the tenant.
  const { nameOf } = useBridgeChatNames();

  // ── Load / dirty tracking ──
  // `baseline` = the form as last loaded or saved; dirty = the form differs from it.
  // `loadedFrom` = the source (agent + policy) it was loaded from; a newer source
  // for the SAME agent reloads silently when clean, else shows the banner.
  const baseline = useRef<string | null>(null);
  const isDirty = baseline.current !== null && baseline.current !== JSON.stringify(form);
  // The signature is the form AS IT WOULD LOAD, not the raw agent object: a field
  // the form doesn't edit (enabled, a live counter, a re-fetched identity) must
  // never raise the "changed remotely" banner.
  const sourceForm = formFrom(agent, effectivePolicy);
  const sourceSig = JSON.stringify(sourceForm);
  const loadedFrom = useRef<{ id: string; sig: string } | null>(null);
  // Set by a successful save: the very next version the server sends back is OUR
  // save, so it loads silently even where it differs from what was typed (it
  // would otherwise raise the "changed remotely" banner against the user's own save).
  const acceptNextSource = useRef(false);

  const loadForm = () => {
    loadedFrom.current = { id: agentId ?? '', sig: sourceSig };
    baseline.current = sourceSig;
    acceptNextSource.current = false;
    setForm(sourceForm);
    setRemoteChanged(false);
  };

  useEffect(() => {
    const prev = loadedFrom.current;
    if (prev?.id === (agentId ?? '')) {
      if (prev.sig === sourceSig) return;
      if (isDirty && !acceptNextSource.current) { setRemoteChanged(true); return; }
    } else {
      // Another agent (or a just-created one): reset the transient UI too.
      setPicked({});
      setShowAdvanced(false);
      setErrors({});
      setSubmitError(null);
    }
    loadForm();
    // Reacts to the source CONTENT (sourceSig), not object identity; loadForm and
    // isDirty are read fresh from this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, sourceSig]);

  // ── Chat pickers (shared WhatsApp contact picker: avatars, tabs, search, numbers) ──
  const contactsOf = (ids: string[]): SelectedContact[] => ids.map((id) => picked[id] ?? { id, name: nameOf(id) });
  const chatPicker = (field: ChatListField, helperText: string, height: number, error?: string) => (
    <>
      <ContactPickerField
        collapsible
        showRefreshButton
        searchBoxClassName={pickerBoxCls}
        value={form[field]}
        selectedContacts={contactsOf(form[field])}
        onContactSelected={(c: SelectedContact) => {
          setPicked((p) => ({ ...p, [c.id]: c }));
          setForm((f) => (f[field].includes(c.id) ? f : { ...f, [field]: [...f[field], c.id] }));
        }}
        onContactRemoved={(id: string) => setForm((f) => ({ ...f, [field]: f[field].filter((x) => x !== id) }))}
        height={height}
        searchPlaceholder={t('myBridge.form.chatsSearchPlaceholder')}
        helperText={helperText}
      />
      <ErrorText error={error} />
      {form[field].length > 0 && (
        <p className={hintCls}>{t('myBridge.form.chatsSelected', { count: form[field].length })}</p>
      )}
    </>
  );

  const checkRow = (
    key: KeysOf<boolean>,
    label: string,
    opts: { strong?: boolean; testId?: string; className?: string; tip?: string } = {},
  ) => (
    <div className={`flex w-fit items-center gap-1.5 ${opts.className ?? ''}`}>
      <label className="flex items-center gap-2 cursor-pointer">
        <Checkbox checked={form[key] === true} onCheckedChange={(v) => set({ [key]: v === true } as Partial<FormState>)} data-testid={opts.testId} />
        <span className={`text-sm text-ai-text ${opts.strong ? 'font-medium' : ''}`}>{label}</span>
      </label>
      {opts.tip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" className="text-ai-text-secondary hover:text-ai-text" aria-label={opts.tip}>
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs whitespace-pre-line">{opts.tip}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );

  /** A labelled toggle (Switch) with a hint underneath. */
  const switchRow = (key: KeysOf<boolean>, label: string, hint: string, testId: string, disabled = false) => (
    <div className="space-y-0.5">
      <label className={`flex items-center justify-between gap-3 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
        <span className="text-sm font-medium text-ai-text">{label}</span>
        <Switch
          checked={form[key] === true}
          disabled={disabled}
          onCheckedChange={(v) => set({ [key]: v === true } as Partial<FormState>)}
          // The theme's Switch has no checked fill of its own (same as the agent on/off toggle).
          style={form[key] === true ? { backgroundColor: 'var(--ai-accent)' } : undefined}
          data-testid={testId}
        />
      </label>
      <p className={hintCls}>{hint}</p>
    </div>
  );

  const radioGroup = <K extends 'chatMode' | 'sendMode'>(key: K, options: ReadonlyArray<readonly [FormState[K], string]>, testIdPrefix: string) => (
    <div className="space-y-2" data-testid={`agent-${testIdPrefix}`}>
      {options.map(([value, label]) => (
        <label key={value} className="flex w-fit items-center gap-2 cursor-pointer">
          <input
            className="h-4 w-4 accent-[var(--ai-text)]"
            type="radio"
            name={`agent-${testIdPrefix}`}
            checked={form[key] === value}
            onChange={() => set({ [key]: value } as Partial<FormState>)}
            data-testid={`${testIdPrefix}-${value}`}
          />
          <span className="text-sm text-ai-text">{label}</span>
        </label>
      ))}
    </div>
  );

  /** An admin select; `''` in the form maps to the `fallback` option. */
  const optionSelect = (key: 'model' | 'effort' | 'transcribeLang', label: string, fallback: string, options: Array<[string, string]>, testId: string, hideLabel = false, disabled = false) => (
    <div className="space-y-1">
      {/* A select that already sits under a section heading of the same name
          says it once, not twice. */}
      {!hideLabel && <Label className={labelCls}>{label}</Label>}
      <Select dir={dir} disabled={disabled} value={form[key] || fallback} onValueChange={(v) => set({ [key]: v === 'default' ? '' : v } as Partial<FormState>)}>
        <SelectTrigger className={inputCls} data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent {...SELECT_CONTENT_PROPS}>
          {options.map(([value, optionLabel]) => <SelectItem key={value} value={value}>{optionLabel}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const openEditorButton = (onClick: () => void, testId: string) => (
    <button type="button" onClick={onClick} className="flex items-center gap-1 rounded-ai-pill px-2 py-1 text-xs font-medium text-ai-text-secondary transition-colors hover:bg-ai-muted hover:text-ai-text" data-testid={testId}>
      <Maximize2 className="h-3.5 w-3.5" /> {t('myBridge.form.openEditor')}
    </button>
  );

  const validate = (): boolean => {
    const next: FieldErrors = {};
    if (!form.label.trim()) next.label = t('myBridge.form.errorNameRequired');
    if (!form.identityPrompt.trim()) next.identityPrompt = t('myBridge.form.errorInstructionsRequired');
    // Incoming trigger chats are required ONLY when the agent is incoming-triggered.
    // A time-triggered-only agent (incoming off) legitimately answers no incoming chats.
    if (form.incomingEnabled) {
      if (form.chatMode === 'list' && form.chatIds.length === 0 && !form.includeNewChats) next.chatIds = t('myBridge.form.errorChatsRequired');
    }
    if (form.sendMode === 'list' && form.sendChatIds.length === 0) next.sendChatIds = t('myBridge.send.errorRequired');
    // rev 3.3 §6.8: an operator chat is never one of the agent's customer chats.
    if (form.operatorChatId && form.incomingEnabled && form.chatMode === 'list' && form.chatIds.includes(form.operatorChatId)) {
      next.operatorChatId = t('myBridge.operator.errorIsCustomerChat');
    }
    const quietErr = quietHoursError(form);
    if (quietErr) next.quietHours = t(`myBridge.quietHours.error.${quietErr}`);
    if (autoResumeInvalid(form)) next.autoResume = t('myBridge.takeover.autoResumeError', { min: AUTO_RESUME_MIN, max: AUTO_RESUME_MAX });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setJustSaved(false);
    if (!validate()) return;
    const f = form;
    // A published Tik must answer everyone: closing the agent to "all chats" hides it from the marketplace.
    const answersAll = (x: FormState) => x.incomingEnabled && x.chatMode === 'all_except';
    if (agentTik?.tik?.published && answersAll(sourceForm) && !answersAll(f) && !window.confirm(t('myBridge.tik.hideWarning'))) return;
    const payload = buildAgentPayload(f, sourceForm, !!isAdmin);

    try {
      // 1) Save the agent (create → get its id; edit → its existing id).
      const savedId = agentId
        ? (await updateAgent({ id: agentId, ...payload }).unwrap()).id ?? agentId
        : (await createAgent({ ...payload, ...(mcpPicks.length ? { mcpServers: mcpPicks } : {}) }).unwrap()).id;
      // 2) Save its capability contract (incoming trigger / tools / read). The READ
      //    policy only means something with tools on; off clears it (null).
      if (savedId) await setAgentPolicy({ agentId: savedId, ...buildPolicyPayload(f) }).unwrap();
      // FE#484: release the paused chats the owner ticked, once the agent itself saved.
      if (agentId && alsoResumePaused && !f.takeoverByIncoming && resumePausedRef.current) {
        await resumePausedRef.current();
        setAlsoResumePaused(false);
      }
      if (!agentId) setMcpPicks([]);
      // 3) A new agent: what was attached before the first save goes up now, with its id.
      //    The agent IS saved either way — a failure here is a toast (it outlives the
      //    navigation to the new agent's page), never a failed save.
      if (!agentId && savedId) {
        const failedFiles = await uploadPendingKnowledgeFiles(uploadKnowledgeFile, savedId, pendingFiles);
        if (failedFiles.length) toast.error(t('myBridge.kb.uploadFailedAfterCreate', { names: failedFiles.join(', ') }));
        setPendingFiles([]);
      }
      // What was just saved is the new clean baseline, and the refetch it triggers
      // is OUR write coming back — load it silently (see `acceptNextSource`).
      baseline.current = JSON.stringify(f);
      acceptNextSource.current = true;
      if (agentId) setJustSaved(true);
      if (savedId) {
        // Durable product event (works in web + extension; see agent-analytics).
        // agent_created here is the FORM-create route (direct AgentForm.createAgent),
        // which is mutually exclusive with the chat-builder create route
        // (useBuilderChat → build job → agent_created): a given agent is born from
        // exactly one of the two, so there is exactly one agent_created per agent. A
        // save on an already-created agent (agentId set) is agent_updated, never a 2nd
        // agent_created.
        reportAgentEvent(agentId ? 'agent_updated' : 'agent_created', 'claude',
          agentId
            ? { agentId: savedId, fields: Object.keys(payload) }
            : { agentId: savedId, model: (payload as { model?: string | null }).model ?? undefined });
        onSaved?.(savedId, !agentId);
      }
    } catch (e) {
      // A 400 says what to fix (e.g. a chat saved as a local number): show it.
      const err = e as { status?: number; data?: { message?: string } };
      setSubmitError((err.status === 400 && err.data?.message) || t('myBridge.form.errorSaveFailed'));
    }
  };

  // Pinned under the fields. In the card it is the card's own sticky footer (inside the one
  // scroll region), so it always lines up with the card exactly.
  // Narrow screens: the end padding keeps Save clear of the Crisp launcher (bottom corner).
  const saveBar = (
    <div
      className={`shrink-0 flex flex-row items-center justify-end gap-3 ps-5 sm:ps-6 pe-24 md:pe-6 py-4 ${card ? 'sticky bottom-0 -mx-5 sm:-mx-10 bg-ai-surface' : ''}`}
    >
      {/* Save error lives in the pinned footer so it's visible without scrolling. */}
      {submitError && <p className="text-sm text-red-500 me-auto">{submitError}</p>}
      {/* One clear primary action: the Agents 2.0 ink pill, for Create and Save alike. */}
      <Button
        variant="ai"
        onClick={handleSubmit}
        disabled={isSaving || policyUnknown}
        className={`min-w-[140px] h-11 rounded-ai-pill px-7 font-semibold transition-colors duration-300 ${
          justSaved ? '!bg-green-600 !text-white !opacity-100' : ''
        }`}
        data-testid="agent-save-button"
        data-saved={justSaved || undefined}
      >
        {justSaved ? (
          <>
            <Check className="h-4 w-4 me-2 animate-in zoom-in-50 spin-in-90 duration-300" strokeWidth={3} />
            {t('myBridge.form.saved')}
          </>
        ) : (
          <>
            {(isSaving || policyUnknown) && <Loader2 className="h-4 w-4 animate-spin me-2" />}
            {agentId ? t('myBridge.form.saveChanges') : t('myBridge.form.create')}
          </>
        )}
      </Button>
    </div>
  );

  // The pane: a flex column that does NOT scroll itself — the fields scroll, the
  // save bar stays pinned under them (one scroll region per pane).
  return (
    <>
      <div className="flex flex-col h-full min-h-0" dir={dir} data-testid="agent-form">
        <div className={`flex-1 min-h-0 overflow-y-auto ${card ? '' : 'px-5 sm:px-10 pt-8 pb-10'}`} data-testid="agent-form-scroll">
          <div className={card ? `${CARD_COLUMN} min-h-full flex flex-col rounded-t-[28px] px-5 sm:px-10 pt-8` : 'contents'}>
          {/* One calm column: a max width keeps lines readable on wide screens. */}
          <div className={`mx-auto w-full max-w-[640px] space-y-10 ${card ? 'flex-1 pb-10' : ''}`}>
          {remoteChanged && (
            <div
              className="flex items-center gap-3 rounded-xl bg-ai-accent-soft px-4 py-3 text-sm text-ai-text"
              data-testid="agent-remote-changed"
            >
              <span className="flex-1">{t('myBridge.builderChat.remoteChanged')}</span>
              <Button type="button" size="sm" variant="outline" onClick={loadForm}>
                {t('myBridge.builderChat.loadLatest')}
              </Button>
            </div>
          )}
          {/* Name */}
          <div className="space-y-8">
            <div className="space-y-2">
              <Label htmlFor="agent-name" className={labelCls}>{t('myBridge.form.nameLabel')}</Label>
              <Input
                id="agent-name"
                value={form.label}
                onChange={(e) => set({ label: e.target.value })}
                placeholder={t('myBridge.form.namePlaceholder')}
                dir={textDir(form.label)}
                style={{ unicodeBidi: 'plaintext' }}
                className={inputCls}
                data-testid="agent-name-input"
              />
              <ErrorText error={errors.label} />
            </div>
          </div>

          {/* Instructions */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="agent-instructions" className={labelCls}>{t('myBridge.form.instructionsLabel')}</Label>
              {openEditorButton(() => setPromptEditorOpen(true), 'agent-prompt-expand')}
            </div>
            <Textarea
              id="agent-instructions"
              value={form.identityPrompt}
              onChange={(e) => set({ identityPrompt: e.target.value })}
              placeholder={t('myBridge.form.instructionsPlaceholder')}
              dir={textDir(form.identityPrompt)}
              // Grows with the text up to exactly 16 lines (16 × 20px text-sm lines
              // + 16px padding + 2px border = 338px); only past that does it scroll.
              className={`field-sizing-content min-h-[160px] max-h-[360px] resize-none ${textareaCls}`}
              data-testid="agent-instructions-input"
            />
            <ErrorText error={errors.identityPrompt} />
          </div>

          {/* ═══ TRIGGERS — what wakes the agent (incoming messages and/or time) ═══ */}
          <div className="space-y-5">
            <div className={sectionTitleCls}>{t('myBridge.triggers.sectionTitle')}</div>

            {/* Incoming-message trigger */}
            <div className="space-y-2" data-testid="agent-incoming-trigger">
              {checkRow('incomingEnabled', t('myBridge.triggers.incomingLabel'), { strong: true, testId: 'incoming-enabled' })}
              <p className={`${hintCls} ps-6`}>{t('myBridge.triggers.incomingHint')}</p>

              {form.incomingEnabled && (
                <div className="space-y-2 ps-6" data-testid="agent-chats-picker">
                  {radioGroup('chatMode', [
                    ['list', t('myBridge.form.scopeAllowList')],
                    ['all_except', t('myBridge.form.scopeAllExcept')],
                  ], 'chat-mode')}
                  {chatPicker(
                    'chatIds',
                    form.chatMode === 'all_except'
                      ? t('myBridge.form.chatsHelperBlock')
                      : t('myBridge.form.chatsHelper'),
                    320,
                    errors.chatIds,
                  )}
                  {checkRow('includeNewChats', t('myBridge.form.respondNewChats'), { className: 'pt-1' })}
                  {checkRow('privateChatsOnly', t('myBridge.form.privateChatsOnly'), { testId: 'private-chats-only', tip: t('myBridge.form.privateChatsOnlyHint') })}
                </div>
              )}
            </div>

            {/* Time trigger — the wake-up schedule(s) */}
            <div className="space-y-2">
              <div className={labelCls}>{t('myBridge.triggers.timeLabel')}</div>
              <p className={hintCls}>{t('myBridge.triggers.timeHint')}</p>
              {agentId ? (
                <TimeTriggersEditor agentId={agentId} schedules={effectiveSchedules} />
              ) : (
                <p className={`${hintCls} italic`}>{t('myBridge.triggers.timeAfterSave')}</p>
              )}
            </div>
          </div>

          {/* ═══ SEND — where the agent may send (separate from what triggers it) ═══ */}
          <div className={sectionCls} data-testid="agent-send-targets">
            <div className={sectionTitleCls}>{t('myBridge.send.sectionTitle')}</div>
            {radioGroup('sendMode', [
              ['same', t('myBridge.send.same')],
              ['list', t('myBridge.form.scopeAllowList')],
              ['all_except', t('myBridge.form.scopeAllExcept')],
            ], 'send-mode')}
            {form.sendMode !== 'same' && (
              <div className="space-y-2 ps-6" data-testid="agent-send-picker">
                {chatPicker(
                  'sendChatIds',
                  form.sendMode === 'all_except' ? t('myBridge.send.helperBlock') : t('myBridge.send.helper'),
                  300,
                  errors.sendChatIds,
                )}
              </div>
            )}
          </div>

          {/* ═══ TAKEOVER — rev 3.3 §6.7: defaults inherited by NEW conversations
              (each conversation then keeps its own; see the inspector). ═══ */}
          <div className={sectionCls} data-testid="agent-takeover">
            <div className={sectionTitleCls}>{t('myBridge.takeover.sectionTitle')}</div>
            {switchRow('takeoverEnabled', t('myBridge.takeover.enabled'), t('myBridge.takeover.enabledHint'), 'takeover-enabled')}
            {switchRow(
              'takeoverByIncoming',
              t('myBridge.takeover.byIncomingMessage'),
              t('myBridge.takeover.byIncomingMessageHint'),
              'takeover-by-incoming',
              !form.takeoverEnabled,
            )}
            {agentId && !form.takeoverByIncoming && (
              <PausedOfferField agentId={agentId} checked={alsoResumePaused} onChange={setAlsoResumePaused} resumeRef={resumePausedRef} />
            )}
            {/* #1059: give the chat back by itself once the owner has been quiet for N minutes. Only meaningful for AUTOMATIC takeover. */}
            <div className="space-y-0.5">
              <label className={`flex items-center justify-between gap-3 ${!form.takeoverEnabled || !form.takeoverByIncoming ? 'opacity-60' : 'cursor-pointer'}`}>
                <span className="text-sm font-medium text-ai-text">{t('myBridge.takeover.autoResume')}</span>
                <Switch
                  checked={form.autoResumeEnabled}
                  disabled={!form.takeoverEnabled || !form.takeoverByIncoming}
                  onCheckedChange={(v) =>
                    set({ autoResumeEnabled: v === true, autoResumeMinutes: v === true && !form.autoResumeMinutes ? String(AUTO_RESUME_DEFAULT_MINUTES) : form.autoResumeMinutes })
                  }
                  style={form.autoResumeEnabled ? { backgroundColor: 'var(--ai-accent)' } : undefined}
                  data-testid="takeover-auto-resume"
                />
              </label>
              <p className={hintCls}>{t('myBridge.takeover.autoResumeHint')}</p>
              {form.autoResumeEnabled && form.takeoverEnabled && form.takeoverByIncoming && (
                <div className="flex items-center gap-2 ps-1">
                  <Label htmlFor="takeover-auto-resume-minutes" className={labelCls}>{t('myBridge.takeover.autoResumeAfter')}</Label>
                  <Input
                    id="takeover-auto-resume-minutes"
                    type="number"
                    inputMode="numeric"
                    min={AUTO_RESUME_MIN}
                    max={AUTO_RESUME_MAX}
                    step={1}
                    value={form.autoResumeMinutes}
                    onChange={(e) => set({ autoResumeMinutes: e.target.value })}
                    className={`${inputCls} w-24`}
                    dir="ltr"
                    data-testid="takeover-auto-resume-minutes"
                  />
                  <span className="text-sm text-ai-text-secondary">{t('myBridge.takeover.autoResumeMinutes')}</span>
                </div>
              )}
              <ErrorText error={errors.autoResume} />
            </div>
          </div>

          {/* ═══ QUIET HOURS — #464: silent weekly windows, no away message ═══ */}
          <div className={sectionCls} data-testid="agent-quiet-hours">
            <div className={sectionTitleCls}>{t('myBridge.quietHours.sectionTitle')}</div>
            {switchRow('quietEnabled', t('myBridge.quietHours.enabled'), t('myBridge.quietHours.enabledHint'), 'quiet-hours-enabled')}
            {form.quietEnabled && (
              <div className="space-y-3 ps-1" data-testid="quiet-hours-editor">
                <div className="space-y-1">
                  <Label htmlFor="quiet-hours-tz" className={labelCls}>{t('myBridge.quietHours.timezone')}</Label>
                  <Input
                    id="quiet-hours-tz"
                    list="quiet-hours-tz-list"
                    value={form.quietTz}
                    onChange={(e) => set({ quietTz: e.target.value.trim() })}
                    className={inputCls}
                    dir="ltr"
                    data-testid="quiet-hours-tz"
                  />
                  <datalist id="quiet-hours-tz-list">
                    {supportedTimeZones().map((z) => (
                      <option key={z} value={z} />
                    ))}
                  </datalist>
                </div>
                {form.quietWindows.map((w, i) => (
                  <div key={i} className="space-y-2 rounded-xl bg-ai-muted p-3" data-testid={`quiet-window-${i}`}>
                    <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('myBridge.quietHours.days')}>
                      {QUIET_DAYS.map((d) => {
                        const on = w.days.includes(d);
                        return (
                          <button
                            key={d}
                            type="button"
                            aria-pressed={on}
                            onClick={() => setQuietWindow(i, { days: on ? w.days.filter((x) => x !== d) : [...w.days, d] })}
                            className={`h-8 min-w-11 rounded-full px-2.5 text-[13px] font-medium transition-colors ${on ? 'bg-ai-accent text-white' : 'bg-ai-surface text-ai-text-secondary hover:text-ai-text'}`}
                            data-testid={`quiet-window-${i}-day-${d}`}
                          >
                            {t(`myBridge.quietHours.day.${d}`)}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        type="time"
                        aria-label={t('myBridge.quietHours.from')}
                        value={w.from}
                        onChange={(e) => setQuietWindow(i, { from: e.target.value })}
                        className={`${inputCls} w-32`}
                        dir="ltr"
                        data-testid={`quiet-window-${i}-from`}
                      />
                      <span className="text-sm text-ai-text-secondary">{t('myBridge.quietHours.until')}</span>
                      <Input
                        type="time"
                        aria-label={t('myBridge.quietHours.to')}
                        value={w.to}
                        onChange={(e) => setQuietWindow(i, { to: e.target.value })}
                        className={`${inputCls} w-32`}
                        dir="ltr"
                        data-testid={`quiet-window-${i}-to`}
                      />
                      {w.from && w.to && w.to < w.from && <span className={hintCls}>{t('myBridge.quietHours.nextDay')}</span>}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="ms-auto"
                        onClick={() => set({ quietWindows: form.quietWindows.filter((_, j) => j !== i) })}
                        data-testid={`quiet-window-${i}-remove`}
                      >
                        {t('myBridge.quietHours.removeWindow')}
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={form.quietWindows.length >= QUIET_HOURS_MAX_WINDOWS}
                  onClick={() => set({ quietWindows: [...form.quietWindows, newQuietWindow()] })}
                  data-testid="quiet-hours-add-window"
                >
                  {t('myBridge.quietHours.addWindow')}
                </Button>
              </div>
            )}
            <ErrorText error={errors.quietHours} />
          </div>

          {/* ═══ OPERATOR CHAT — rev 3.3 §6.8 control plane (one chat) ═══ */}
          <div className={sectionCls} data-testid="agent-operator-chat">
            <div className={sectionTitleCls}>{t('myBridge.operator.sectionTitle')}</div>
            <p className={hintCls}>{t('myBridge.operator.hint')}</p>
            <ContactPickerField
              collapsible
              showRefreshButton
              searchBoxClassName={pickerBoxCls}
              value={form.operatorChatId ? [form.operatorChatId] : []}
              selectedContacts={contactsOf(form.operatorChatId ? [form.operatorChatId] : [])}
              onContactSelected={(c: SelectedContact) => {
                // ONE operator chat: picking another replaces it.
                setPicked((p) => ({ ...p, [c.id]: c }));
                set({ operatorChatId: c.id });
              }}
              onContactRemoved={() => set({ operatorChatId: '' })}
              height={260}
              searchPlaceholder={t('myBridge.form.chatsSearchPlaceholder')}
              helperText={t('myBridge.operator.helper')}
            />
            <ErrorText error={errors.operatorChatId} />
          </div>

          {/* ═══ TOOLS ACCESS — the READ-ONLY Blueticks MCP (sending is the Bridge's job) ═══ */}
          <div className={sectionCls}>
            <div className={sectionTitleCls}>{t('myBridge.tools.sectionTitle')}</div>
            {checkRow('toolsWhatsappMcp', t('myBridge.tools.whatsappMcp'), { strong: true, testId: 'tools-whatsapp-mcp' })}
            <p className={`${hintCls} ps-6`}>{t('myBridge.tools.whatsappMcpHint')}</p>

            {form.toolsWhatsappMcp && (
              <div className="space-y-2 ps-6 pt-1" data-testid="agent-read-caps">
                {checkRow('readMessages', t('myBridge.tools.readMessages'), { testId: 'read-messages' })}
                {form.readMessages && (
                  <div className="space-y-1 ps-6" data-testid="agent-read-msg-picker">
                    <p className={hintCls}>{t('myBridge.tools.readMessagesScopeHint')}</p>
                    {chatPicker('readMsgChatIds', t('myBridge.tools.readMessagesScopeHelper'), 300)}
                  </div>
                )}
              </div>
            )}

            {/* Voice replies — the agent's `voice` tool (answer a voice note with a voice note). */}
            <div className="pt-2">
              {checkRow('toolsVoice', t('myBridge.tools.voice'), { strong: true, testId: 'tools-voice' })}
              <p className={`${hintCls} ps-6`}>{t('myBridge.tools.voiceHint')}</p>
              {form.toolsVoice && (
                <div className="space-y-1 ps-6 pt-1">
                  <Label htmlFor="agent-voice-when" className={labelCls}>{t('myBridge.tools.voiceWhen')}</Label>
                  <Textarea
                    id="agent-voice-when"
                    value={form.voiceWhen}
                    onChange={(e) => set({ voiceWhen: e.target.value })}
                    placeholder={t('myBridge.tools.voiceWhenPlaceholder')}
                    maxLength={1000}
                    rows={2}
                    className={textareaCls}
                    data-testid="tools-voice-when"
                  />
                  <p className={hintCls}>{t('myBridge.tools.voiceWhenHint')}</p>
                </div>
              )}
            </div>

            {/* Voice calls — a call to one of its chats is answered by this agent, live. */}
            <div className="pt-2">
              {checkRow('toolsCalls', t('myBridge.tools.calls'), { strong: true, testId: 'tools-calls' })}
              <p className={`${hintCls} ps-6`}>{t('myBridge.tools.callsHint')}</p>
            </div>

            {/* Boards — the agent opens and keeps its OWN boards (tasks, leads, follow-ups…). */}
            <div className="pt-2">
              {checkRow('toolsBoards', t('myBridge.tools.boards'), { strong: true, testId: 'tools-boards' })}
              <p className={`${hintCls} ps-6`}>{t('myBridge.tools.boardsHint')}</p>
              {form.toolsBoards && (
                <div className="ps-6 pt-1">
                  <AgentBoardsSection
                    boards={effectivePolicy?.boards ?? []}
                    accountBoards={effectivePolicy?.accountBoards ?? []}
                    pending={form.adoptBoards}
                    onChange={(adoptBoards) => set({ adoptBoards })}
                    dir={dir}
                    classes={{ label: labelCls, hint: hintCls, input: inputCls }}
                  />
                </div>
              )}
            </div>

            {/* The voice it speaks with — one choice for its voice notes and its calls. */}
            {(form.toolsVoice || form.toolsCalls) && (
              <div className="pt-2">
                <VoicePicker value={form.voiceName} onChange={(voiceName) => set({ voiceName })} dir={dir} classes={{ label: labelCls, hint: hintCls, input: inputCls }} />
              </div>
            )}

            {/* Creative tools — songs / images made into the chat the agent answers. */}
            <div className="space-y-1 pt-3" data-testid="agent-creative-tools">
              <p className="text-sm font-medium text-ai-text">{t('myBridge.tools.creativeTitle')}</p>
              <p className={hintCls}>{t('myBridge.tools.creativeHint')}</p>
              {checkRow('toolsSuno', t('myBridge.tools.songs'), { testId: 'tools-songs', tip: t('myBridge.tools.songsHint') })}
              {checkRow('toolsImages', t('myBridge.tools.images'), { testId: 'tools-images', tip: t('myBridge.tools.imagesHint') })}
            </div>
          </div>

          {/* ═══ INTEGRATIONS — external MCP servers, every tool off until ticked (hidden while the backend switch is off) ═══ */}
          <McpServersSection
            agentId={agentId ?? undefined}
            publicAgent={form.incomingEnabled && form.chatMode === 'all_except'}
            classes={{ section: sectionCls, title: sectionTitleCls, label: labelCls, hint: hintCls, input: inputCls }}
            picks={mcpPicks}
            onPicksChange={setMcpPicks}
          />

          {/* ═══ TIK — the agent's public marketplace profile (saved separately from the form) ═══ */}
          {agentId && (
            <TikSection
              agentId={agentId}
              agentLabel={form.label}
              classes={{ section: sectionCls, title: sectionTitleCls, label: labelCls, hint: hintCls, input: inputCls }}
            />
          )}

          {/* ═══ GOOGLE CALENDAR (service account) — LEGACY: only an agent already connected this way keeps it;
              every other agent connects Google Calendar from Integrations above. ═══ */}
          {legacyCalendar?.connected && (
          <div className={sectionCls}>
            <div className={sectionTitleCls}>{t('myBridge.calendar.sectionTitle')}</div>
            <GoogleCalendarSection
              agentId={agentId!}
              classes={{ label: labelCls, hint: hintCls, input: inputCls, textarea: textareaCls }}
            />
          </div>
          )}

          {/* Optional knowledge */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="agent-knowledge" className={labelCls}>{t('myBridge.form.knowledgeLabel')}</Label>
              {openEditorButton(() => setKnowledgeEditorOpen(true), 'agent-knowledge-expand')}
            </div>
            <Textarea
              id="agent-knowledge"
              value={form.knowledgeText}
              onChange={(e) => set({ knowledgeText: e.target.value })}
              placeholder={t('myBridge.form.knowledgePlaceholder')}
              rows={6}
              dir={textDir(form.knowledgeText)}
              className={`min-h-[120px] max-h-[280px] resize-y ${textareaCls}`}
              data-testid="agent-knowledge-input"
            />
            {checkRow('knowledgeOnly', t('myBridge.form.knowledgeOnly'), { className: 'pt-1', tip: t('myBridge.form.knowledgeOnlyTip') })}
            {checkRow('stayOnTopic', t('myBridge.form.stayOnTopic'), { tip: t('myBridge.form.stayOnTopicTip') })}
            {/* Knowledge FILES — a new agent's are held here and uploaded on its first save. */}
            <div className="pt-3">
              <KnowledgeFilesManager agentId={agentId} pending={pendingFiles} onPendingChange={setPendingFiles} isRtl={isRtl} />
            </div>
          </div>

          {/* Advanced settings — everyone. Model / effort / level / timeout are
              operator-only (the Bridge rejects them from a customer), so only an
              admin sees those; voice, the bot marker and web access are the owner's. */}
          <div>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-ai-pill px-3 py-1.5 -ms-3 text-sm font-medium text-ai-text-secondary transition-colors hover:bg-ai-muted hover:text-ai-text"
              onClick={() => setShowAdvanced((s) => !s)}
              aria-expanded={showAdvanced}
              data-testid="agent-advanced-toggle"
            >
              {showAdvanced ? t('myBridge.form.advancedHide') : t('myBridge.form.advancedShow')}
              <ChevronDown className={`h-4 w-4 transition-transform duration-(--ai-duration-fast) ${showAdvanced ? 'rotate-180' : ''}`} />
            </button>
            {showAdvanced && (
              <div className="space-y-5 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  {isAdmin && optionSelect('model', t('myBridge.form.model'), 'default', [
                    ['default', t('myBridge.form.modelDefault')],
                    ['opus', 'Opus'],
                    ['sonnet', 'Sonnet'],
                    ['haiku', 'Haiku'],
                  ], 'agent-model-select')}
                  {isAdmin && optionSelect('effort', t('myBridge.form.effort'), 'default', [
                    ['default', t('myBridge.form.effortDefault')],
                    ['low', t('myBridge.form.effortFast')],
                    ['medium', t('myBridge.form.effortMedium')],
                    ['high', t('myBridge.form.effortSlow')],
                    ['max', t('myBridge.form.effortMax')],
                  ], 'agent-effort-select')}
                  {optionSelect('transcribeLang', t('myBridge.form.voice'), 'default', [
                    ['default', t('myBridge.form.voiceDefault')],
                    ['he', t('myBridge.form.voiceHebrew')],
                    ['auto', t('myBridge.form.voiceAuto')],
                  ], 'agent-voice-select')}
                </div>
                {checkRow('hideBotPrefix', t('myBridge.form.hideMarker'))}

                {/* WEB ACCESS — whether the agent can read the internet (WebFetch). Off
                    by default; allow-list limits it to named sites; all opens any site.
                    Cloud-metadata / loopback hosts are always blocked (SSRF). */}
                <div className="space-y-2 pt-2">
                  <Label className={labelCls}>{t('myBridge.form.webAccess')}</Label>
                  <Select dir={dir} value={form.webMode} onValueChange={(v) => set({ webMode: v as WebMode })}>
                    <SelectTrigger className={inputCls} data-testid="agent-webaccess-select"><SelectValue /></SelectTrigger>
                    <SelectContent {...SELECT_CONTENT_PROPS}>
                      <SelectItem value="off">{t('myBridge.form.webOff')}</SelectItem>
                      <SelectItem value="allowlist">{t('myBridge.form.webAllowlist')}</SelectItem>
                      <SelectItem value="all">{t('myBridge.form.webAll')}</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.webMode === 'allowlist' && (
                    <div className="space-y-1">
                      <Textarea
                        value={form.webHosts}
                        onChange={(e) => set({ webHosts: e.target.value })}
                        placeholder={t('myBridge.form.webHostsPlaceholder', 'example.com\nwww.example.com')}
                        rows={3}
                        className={textareaCls}
                        data-testid="agent-webaccess-hosts"
                        dir="ltr"
                      />
                      <p className={hintCls}>{t('myBridge.form.webHostsHint')}</p>
                    </div>
                  )}
                  {form.webMode !== 'off' && <p className={hintCls}>{t('myBridge.form.webBlockedNote')}</p>}
                </div>
                {isAdmin && <p className={hintCls}>{t('myBridge.form.advancedNote')}</p>}
              </div>
            )}
          </div>
          </div>
          {card && saveBar}
          </div>
        </div>
        {!card && saveBar}
      </div>

      {/* Big Markdown editors — comfortable editing for long prompt / knowledge. */}
      <MarkdownEditorModal
        open={promptEditorOpen}
        onOpenChange={setPromptEditorOpen}
        title={t('myBridge.form.instructionsLabel')}
        value={form.identityPrompt}
        onSave={(v) => set({ identityPrompt: v })}
        placeholder={t('myBridge.form.instructionsPlaceholder')}
        isRtl={isRtl}
      />
      <MarkdownEditorModal
        open={knowledgeEditorOpen}
        onOpenChange={setKnowledgeEditorOpen}
        title={t('myBridge.form.knowledgeLabel')}
        value={form.knowledgeText}
        onSave={(v) => set({ knowledgeText: v })}
        placeholder={t('myBridge.form.knowledgePlaceholder')}
        isRtl={isRtl}
      />
    </>
  );
};
